import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import toast from "react-hot-toast";
import "../styles/OfferComparison.css";

interface Offer {
  id: string;
  jobTitle: string;
  company: string;
  location: string;
  status: string;
  appliedAt: string;
  interviewDate: string | null;
  notes: string;
  url: string;
  // Additional fields for comparison (can be stored in notes or separate model)
  salary?: string;
  benefits?: string;
  startDate?: string;
  workType?: string; // remote, hybrid, onsite
  pros?: string[];
  cons?: string[];
}

interface OfferDetails {
  id: string;
  salary: string;
  signingBonus: string;
  annualBonus: string;
  equity: string;
  healthInsurance: boolean;
  dentalVision: boolean;
  retirement401k: boolean;
  pto: string;
  workType: string;
  commute: string;
  startDate: string;
  pros: string;
  cons: string;
}

const EMPTY_DETAILS: OfferDetails = {
  id: "",
  salary: "",
  signingBonus: "",
  annualBonus: "",
  equity: "",
  healthInsurance: false,
  dentalVision: false,
  retirement401k: false,
  pto: "",
  workType: "hybrid",
  commute: "",
  startDate: "",
  pros: "",
  cons: "",
};

const OfferComparison: React.FC = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [selectedOffers, setSelectedOffers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [offerDetails, setOfferDetails] = useState<Record<string, OfferDetails>>({});
  const [editingOffer, setEditingOffer] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<OfferDetails>(EMPTY_DETAILS);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get("/auth/jobs/applications?status=offered");
        // Also get accepted offers for comparison
        const acceptedResponse = await axiosInstance.get("/auth/jobs/applications?status=accepted");
        
        const allOffers = [
          ...response.data.applications,
          ...acceptedResponse.data.applications
        ];
        setOffers(allOffers);
        
        // Load saved offer details from localStorage (or could be from backend)
        const savedDetails = localStorage.getItem("offerDetails");
        if (savedDetails) {
          setOfferDetails(JSON.parse(savedDetails));
        }
      } catch (err) {
        toast.error("Failed to load offers");
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, []);

  const toggleOfferSelection = (offerId: string) => {
    setSelectedOffers(prev => {
      if (prev.includes(offerId)) {
        return prev.filter(id => id !== offerId);
      }
      if (prev.length >= 3) {
        toast.error("You can compare up to 3 offers at a time");
        return prev;
      }
      return [...prev, offerId];
    });
  };

  const startEditingOffer = (offer: Offer) => {
    setEditingOffer(offer.id);
    setEditForm(offerDetails[offer.id] || { ...EMPTY_DETAILS, id: offer.id });
  };

  const saveOfferDetails = () => {
    if (!editingOffer) return;
    
    const updatedDetails = {
      ...offerDetails,
      [editingOffer]: editForm,
    };
    
    setOfferDetails(updatedDetails);
    localStorage.setItem("offerDetails", JSON.stringify(updatedDetails));
    setEditingOffer(null);
    toast.success("Offer details saved!");
  };

  const parseSalary = (salary: string): number => {
    const cleaned = salary.replace(/[^0-9.]/g, "");
    return parseFloat(cleaned) || 0;
  };

  const compareOffers = useMemo(() => {
    if (selectedOffers.length < 2) return null;

    const selected = selectedOffers.map(id => {
      const offer = offers.find(o => o.id === id);
      const details = offerDetails[id] || EMPTY_DETAILS;
      return { offer, details };
    });

    // Calculate total compensation
    const totals = selected.map(({ details }) => {
      const base = parseSalary(details.salary);
      const signing = parseSalary(details.signingBonus);
      const bonus = parseSalary(details.annualBonus);
      const equity = parseSalary(details.equity);
      return base + (signing / 4) + bonus + equity; // Spread signing over 4 years
    });

    const maxTotal = Math.max(...totals);
    const winner = totals.indexOf(maxTotal);

    return { selected, totals, winner };
  }, [selectedOffers, offers, offerDetails]);

  const formatCurrency = (value: string | number): string => {
    const num = typeof value === "string" ? parseSalary(value) : value;
    if (!num) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(num);
  };

  if (loading) {
    return (
      <div className="oc-container">
        <div className="oc-loading">Loading offers...</div>
      </div>
    );
  }

  return (
    <div className="oc-container">
      {/* Header */}
      <div className="oc-header">
        <div className="oc-header-left">
          <button className="oc-back-btn" onClick={() => navigate("/applications/kanban")}>
            ← Back to Applications
          </button>
          <h1>💰 Compare Offers</h1>
        </div>
        <p className="oc-subtitle">Select up to 3 offers to compare side-by-side</p>
      </div>

      {offers.length === 0 ? (
        <div className="oc-empty">
          <div className="oc-empty-icon">🎁</div>
          <h2>No offers yet</h2>
          <p>When you receive job offers, they'll appear here for comparison</p>
          <button className="oc-btn oc-btn-primary" onClick={() => navigate("/applications/kanban")}>
            View Applications
          </button>
        </div>
      ) : (
        <>
          {/* Offer Selection Grid */}
          <div className="oc-selection-section">
            <h2>Your Offers ({offers.length})</h2>
            <div className="oc-offers-grid">
              {offers.map(offer => {
                const isSelected = selectedOffers.includes(offer.id);
                const details = offerDetails[offer.id];
                
                return (
                  <div 
                    key={offer.id} 
                    className={`oc-offer-card ${isSelected ? "selected" : ""} ${offer.status === "accepted" ? "accepted" : ""}`}
                    onClick={() => toggleOfferSelection(offer.id)}
                  >
                    <div className="oc-offer-check">
                      {isSelected ? "✓" : ""}
                    </div>
                    {offer.status === "accepted" && (
                      <span className="oc-accepted-badge">Accepted</span>
                    )}
                    <h3>{offer.jobTitle}</h3>
                    <p className="oc-offer-company">{offer.company}</p>
                    <p className="oc-offer-location">📍 {offer.location}</p>
                    {details?.salary && (
                      <p className="oc-offer-salary">{formatCurrency(details.salary)}/year</p>
                    )}
                    <button 
                      className="oc-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditingOffer(offer);
                      }}
                    >
                      ✏️ Edit Details
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Comparison Table */}
          {compareOffers && (
            <div className="oc-comparison-section">
              <h2>Side-by-Side Comparison</h2>
              <div className="oc-comparison-table">
                <table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      {compareOffers.selected.map(({ offer }, idx) => (
                        <th 
                          key={offer?.id} 
                          className={compareOffers.winner === idx ? "winner" : ""}
                        >
                          {offer?.company}
                          {compareOffers.winner === idx && (
                            <span className="winner-badge">Best Value</span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="section-header">
                      <td colSpan={compareOffers.selected.length + 1}>💵 Compensation</td>
                    </tr>
                    <tr>
                      <td>Position</td>
                      {compareOffers.selected.map(({ offer }) => (
                        <td key={offer?.id}>{offer?.jobTitle}</td>
                      ))}
                    </tr>
                    <tr>
                      <td>Base Salary</td>
                      {compareOffers.selected.map(({ offer, details }) => (
                        <td key={offer?.id} className="money">
                          {formatCurrency(details.salary)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>Signing Bonus</td>
                      {compareOffers.selected.map(({ offer, details }) => (
                        <td key={offer?.id} className="money">
                          {formatCurrency(details.signingBonus)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>Annual Bonus</td>
                      {compareOffers.selected.map(({ offer, details }) => (
                        <td key={offer?.id} className="money">
                          {formatCurrency(details.annualBonus)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>Equity/RSUs</td>
                      {compareOffers.selected.map(({ offer, details }) => (
                        <td key={offer?.id} className="money">
                          {formatCurrency(details.equity)}
                        </td>
                      ))}
                    </tr>
                    <tr className="total-row">
                      <td>Total Comp (Est.)</td>
                      {compareOffers.totals.map((total, idx) => (
                        <td key={idx} className={`money ${compareOffers.winner === idx ? "winner" : ""}`}>
                          {formatCurrency(total)}
                        </td>
                      ))}
                    </tr>

                    <tr className="section-header">
                      <td colSpan={compareOffers.selected.length + 1}>🏥 Benefits</td>
                    </tr>
                    <tr>
                      <td>Health Insurance</td>
                      {compareOffers.selected.map(({ offer, details }) => (
                        <td key={offer?.id}>
                          {details.healthInsurance ? "✅ Yes" : "❌ No"}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>Dental/Vision</td>
                      {compareOffers.selected.map(({ offer, details }) => (
                        <td key={offer?.id}>
                          {details.dentalVision ? "✅ Yes" : "❌ No"}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>401(k)/Retirement</td>
                      {compareOffers.selected.map(({ offer, details }) => (
                        <td key={offer?.id}>
                          {details.retirement401k ? "✅ Yes" : "❌ No"}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>PTO/Vacation</td>
                      {compareOffers.selected.map(({ offer, details }) => (
                        <td key={offer?.id}>{details.pto || "-"}</td>
                      ))}
                    </tr>

                    <tr className="section-header">
                      <td colSpan={compareOffers.selected.length + 1}>🏢 Work Environment</td>
                    </tr>
                    <tr>
                      <td>Location</td>
                      {compareOffers.selected.map(({ offer }) => (
                        <td key={offer?.id}>{offer?.location}</td>
                      ))}
                    </tr>
                    <tr>
                      <td>Work Type</td>
                      {compareOffers.selected.map(({ offer, details }) => (
                        <td key={offer?.id} className="capitalize">{details.workType || "-"}</td>
                      ))}
                    </tr>
                    <tr>
                      <td>Commute Time</td>
                      {compareOffers.selected.map(({ offer, details }) => (
                        <td key={offer?.id}>{details.commute || "-"}</td>
                      ))}
                    </tr>
                    <tr>
                      <td>Start Date</td>
                      {compareOffers.selected.map(({ offer, details }) => (
                        <td key={offer?.id}>{details.startDate || "-"}</td>
                      ))}
                    </tr>

                    <tr className="section-header">
                      <td colSpan={compareOffers.selected.length + 1}>📝 Notes</td>
                    </tr>
                    <tr>
                      <td>Pros</td>
                      {compareOffers.selected.map(({ offer, details }) => (
                        <td key={offer?.id} className="notes-cell pros">
                          {details.pros || "-"}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>Cons</td>
                      {compareOffers.selected.map(({ offer, details }) => (
                        <td key={offer?.id} className="notes-cell cons">
                          {details.cons || "-"}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {selectedOffers.length < 2 && selectedOffers.length > 0 && (
            <div className="oc-hint">
              Select at least one more offer to compare
            </div>
          )}
        </>
      )}

      {/* Edit Modal */}
      {editingOffer && (
        <div className="oc-modal-overlay" onClick={() => setEditingOffer(null)}>
          <div className="oc-modal" onClick={(e) => e.stopPropagation()}>
            <button className="oc-modal-close" onClick={() => setEditingOffer(null)}>×</button>
            
            <h2>Edit Offer Details</h2>
            <p className="oc-modal-subtitle">
              {offers.find(o => o.id === editingOffer)?.jobTitle} at{" "}
              {offers.find(o => o.id === editingOffer)?.company}
            </p>

            <form className="oc-edit-form" onSubmit={(e) => { e.preventDefault(); saveOfferDetails(); }}>
              <div className="oc-form-section">
                <h3>💵 Compensation</h3>
                <div className="oc-form-grid">
                  <div className="oc-form-group">
                    <label>Base Salary</label>
                    <input
                      type="text"
                      value={editForm.salary}
                      onChange={(e) => setEditForm(prev => ({ ...prev, salary: e.target.value }))}
                      placeholder="e.g., $120,000"
                    />
                  </div>
                  <div className="oc-form-group">
                    <label>Signing Bonus</label>
                    <input
                      type="text"
                      value={editForm.signingBonus}
                      onChange={(e) => setEditForm(prev => ({ ...prev, signingBonus: e.target.value }))}
                      placeholder="e.g., $10,000"
                    />
                  </div>
                  <div className="oc-form-group">
                    <label>Annual Bonus</label>
                    <input
                      type="text"
                      value={editForm.annualBonus}
                      onChange={(e) => setEditForm(prev => ({ ...prev, annualBonus: e.target.value }))}
                      placeholder="e.g., $15,000"
                    />
                  </div>
                  <div className="oc-form-group">
                    <label>Equity/RSUs (Annual)</label>
                    <input
                      type="text"
                      value={editForm.equity}
                      onChange={(e) => setEditForm(prev => ({ ...prev, equity: e.target.value }))}
                      placeholder="e.g., $20,000"
                    />
                  </div>
                </div>
              </div>

              <div className="oc-form-section">
                <h3>🏥 Benefits</h3>
                <div className="oc-checkbox-grid">
                  <label className="oc-checkbox">
                    <input
                      type="checkbox"
                      checked={editForm.healthInsurance}
                      onChange={(e) => setEditForm(prev => ({ ...prev, healthInsurance: e.target.checked }))}
                    />
                    <span>Health Insurance</span>
                  </label>
                  <label className="oc-checkbox">
                    <input
                      type="checkbox"
                      checked={editForm.dentalVision}
                      onChange={(e) => setEditForm(prev => ({ ...prev, dentalVision: e.target.checked }))}
                    />
                    <span>Dental/Vision</span>
                  </label>
                  <label className="oc-checkbox">
                    <input
                      type="checkbox"
                      checked={editForm.retirement401k}
                      onChange={(e) => setEditForm(prev => ({ ...prev, retirement401k: e.target.checked }))}
                    />
                    <span>401(k)/Retirement</span>
                  </label>
                </div>
                <div className="oc-form-group">
                  <label>PTO/Vacation</label>
                  <input
                    type="text"
                    value={editForm.pto}
                    onChange={(e) => setEditForm(prev => ({ ...prev, pto: e.target.value }))}
                    placeholder="e.g., 20 days + holidays"
                  />
                </div>
              </div>

              <div className="oc-form-section">
                <h3>🏢 Work Details</h3>
                <div className="oc-form-grid">
                  <div className="oc-form-group">
                    <label>Work Type</label>
                    <select
                      value={editForm.workType}
                      onChange={(e) => setEditForm(prev => ({ ...prev, workType: e.target.value }))}
                    >
                      <option value="remote">Remote</option>
                      <option value="hybrid">Hybrid</option>
                      <option value="onsite">On-site</option>
                    </select>
                  </div>
                  <div className="oc-form-group">
                    <label>Commute Time</label>
                    <input
                      type="text"
                      value={editForm.commute}
                      onChange={(e) => setEditForm(prev => ({ ...prev, commute: e.target.value }))}
                      placeholder="e.g., 30 mins"
                    />
                  </div>
                  <div className="oc-form-group">
                    <label>Start Date</label>
                    <input
                      type="text"
                      value={editForm.startDate}
                      onChange={(e) => setEditForm(prev => ({ ...prev, startDate: e.target.value }))}
                      placeholder="e.g., March 15, 2026"
                    />
                  </div>
                </div>
              </div>

              <div className="oc-form-section">
                <h3>📝 Personal Notes</h3>
                <div className="oc-form-group">
                  <label>Pros</label>
                  <textarea
                    value={editForm.pros}
                    onChange={(e) => setEditForm(prev => ({ ...prev, pros: e.target.value }))}
                    placeholder="What do you like about this offer?"
                    rows={3}
                  />
                </div>
                <div className="oc-form-group">
                  <label>Cons</label>
                  <textarea
                    value={editForm.cons}
                    onChange={(e) => setEditForm(prev => ({ ...prev, cons: e.target.value }))}
                    placeholder="Any concerns or drawbacks?"
                    rows={3}
                  />
                </div>
              </div>

              <div className="oc-form-actions">
                <button type="button" className="oc-btn oc-btn-secondary" onClick={() => setEditingOffer(null)}>
                  Cancel
                </button>
                <button type="submit" className="oc-btn oc-btn-primary">
                  Save Details
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferComparison;
